import { TypeIcon } from "../common/TypeIcon";
import { useMemo } from "react";
import { useEntities } from "../../hooks/useEntities";
import { isContainerEntityType } from "../../utils/activityLogging";
import { partitionByActive, type TimeWindow } from "../../utils/entityActive";
import { entityTypeLabel } from "../../utils/tripDisplay";
import type { Entity, EntityType } from "../../types";

type EntityWithId = Entity & { id: string };

/** Enough to scan on a phone; a longer list means the query needs another letter. */
const MAX_RESULTS = 40;

interface LogActivitySearchProps {
  /** What the user has typed. Blank renders nothing. */
  query: string;
  onPick: (entityId: string, type: EntityType) => void;
  disabled?: boolean;
  /**
   * The stretch of time to judge "is this still a place" by — from
   * `useActiveAsOf`, so these results and the nearby strip agree. Places
   * outside it are still findable by name, under their own heading.
   */
  activeDuring?: TimeWindow;
}

function rank(name: string, q: string): number {
  const n = name.toLowerCase();
  if (n.startsWith(q)) return 0;
  // A word boundary beats a match buried mid-word ("mountain" → Space Mountain
  // before "Big Thunder Mountain Railroad" is fine either way, but "sea" should
  // find Seas with Nemo before Chelsea).
  if (n.includes(` ${q}`)) return 1;
  return 2;
}

/**
 * Inline search results for the log sheet.
 *
 * Deliberately not a dropdown: the previous picker floated its matches below
 * the search box, and on a phone that put them under the keyboard. These rows
 * are part of the sheet's own flow, directly beneath the search field at the
 * top, so they sit in the part of the screen the keyboard leaves visible.
 */
export function LogActivitySearch({ query, onPick, disabled, activeDuring }: LogActivitySearchProps) {
  const { entities, loading } = useEntities();
  const q = query.trim().toLowerCase();

  const nameOf = useMemo(() => {
    const byId = new Map<string, string>();
    for (const e of entities) byId.set(e.id, e.displayName);
    return (id: string | null | undefined) => (id ? byId.get(id) ?? null : null);
  }, [entities]);

  const { active, retired, truncated } = useMemo(() => {
    if (!q) return { active: [] as EntityWithId[], retired: [] as EntityWithId[], truncated: false };
    const matching = entities
      .filter((e) => e.displayName.toLowerCase().includes(q))
      .sort((a, b) => rank(a.displayName, q) - rank(b.displayName, q));
    const split = activeDuring
      ? partitionByActive(matching, activeDuring)
      : { active: matching, retired: [] as EntityWithId[] };
    return {
      active: split.active.slice(0, MAX_RESULTS),
      retired: split.retired.slice(0, Math.max(0, MAX_RESULTS - split.active.length)),
      truncated: split.active.length + split.retired.length > MAX_RESULTS,
    };
  }, [entities, q, activeDuring]);

  if (!q) return null;

  if (loading) {
    return <p className="text-muted log-activity-search-empty">Loading places…</p>;
  }

  if (active.length === 0 && retired.length === 0) {
    return (
      <p className="text-muted log-activity-search-empty">
        No places match “{query.trim()}”. Log it as a custom activity below.
      </p>
    );
  }

  const row = (e: EntityWithId, isRetired: boolean) => {
    const container = isContainerEntityType(e.type);
    const parent = nameOf(e.parentId);
    return (
      <button
        key={e.id}
        type="button"
        className={`log-activity-result${isRetired ? " log-activity-result-retired" : ""}`}
        onClick={() => onPick(e.id, e.type)}
        disabled={disabled}
      >
        <span className="log-activity-result-icon" aria-hidden="true">
          {e.imageUrl ? <img src={e.imageUrl} alt="" /> : <TypeIcon type={e.type} name={e.displayName} tile />}
        </span>
        <span className="log-activity-result-text">
          <span className="log-activity-result-name">{e.displayName}</span>
          <span className="log-activity-result-meta">
            {entityTypeLabel(e.type)}
            {parent ? ` · ${parent}` : ""}
          </span>
        </span>
        {container && (
          <span className="log-activity-result-chevron" aria-label="Browse inside">
            ›
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="log-activity-results">
      {active.map((e) => row(e, false))}
      {retired.length > 0 && (
        <>
          <h4 className="log-activity-section-label log-activity-results-retired-label">
            No longer running
          </h4>
          {retired.map((e) => row(e, true))}
        </>
      )}
      {truncated && (
        <p className="text-muted log-activity-search-empty">Keep typing to narrow it down.</p>
      )}
    </div>
  );
}
